/**
 * Note Top Bar Transcript Component
 *
 * Top bar for transcript view mode
 * - Left section: "View Note" button to return to note view
 * - Right section: Empty (could add transcript-specific actions later)
 * - Menu: Delete Note
 *
 * Used when user switches from note view to transcript view
 */

import React, { useMemo, useState } from 'react';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { Article as ArticleIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useEventTracking } from '@services/statsig/hooks/useEventTracking';

import type { MenuItemConfig } from './layouts/TopBarLayout';
import { TopBarLayout } from './layouts/TopBarLayout';

export interface NoteTopBarTranscriptProps {
  /**
   * Note ID for context
   */
  noteId: string;

  /**
   * Encounter ID for analytics
   */
  encounterId?: string;

  /**
   * Handler for switching back to note view
   */
  onViewNote: () => void;

  /**
   * Handler for deleting the note
   */
  onDeleteNote?: () => Promise<void>;
}

/**
 * Top bar for transcript view
 * Provides navigation back to note view and standard actions
 */
export const NoteTopBarTranscript: React.FC<NoteTopBarTranscriptProps> = ({
  noteId,
  encounterId,
  onViewNote,
  onDeleteNote,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { note } = useEventTracking();

  const handleDeleteClick = () => {
    note.topbarDeleteClicked(noteId, {
      view_mode: 'transcript',
      is_signed: false,
      encounter_id: encounterId,
    });
    setDeleteDialogOpen(true);
  };

  const handleMenuOpen = () => {
    note.topbarMenuOpened(noteId, {
      view_mode: 'transcript',
      encounter_id: encounterId,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteNote) return;

    setIsDeleting(true);
    try {
      await onDeleteNote();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete note:', error);
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Build menu items configuration
  const menuItems: MenuItemConfig[] = useMemo(
    () => [
      {
        label: 'Delete Note',
        icon: <DeleteIcon fontSize="small" />,
        onClick: handleDeleteClick,
        disabled: !onDeleteNote,
      },
      // TODO: Add other transcript-specific menu items as needed
      // e.g., Copy Transcript, Export Transcript, etc.
    ],
    [onDeleteNote],
  );

  // Left section: Empty for now (could add transcript-specific actions later)
  const leftSection = undefined;

  // Right section: With "View Note" button
  const rightSection = (
    <Button
      variant="contained"
      onClick={onViewNote}
      startIcon={<ArticleIcon />}
      sx={{
        textTransform: 'none',
        fontWeight: 500,
      }}
    >
      View Note
    </Button>
  );

  return (
    <>
      <TopBarLayout
        left={leftSection}
        right={rightSection}
        menuItems={menuItems}
        onMenuOpen={handleMenuOpen}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        variant="warning"
        title="Delete Note?"
        message="This action cannot be undone. Are you sure you want to delete this note?"
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        loading={isDeleting}
      />
    </>
  );
};
